import React from 'react';
import { Box, Container, Typography, Grid, Paper, List, ListItem, ListItemIcon, ListItemText, Avatar, Button } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { brand, pageHeroRichSxFor, pageElevatedCardSx, pageHeroImages, pageHeroType } from '../brandTheme';

const Insurance = () => {
  return (
    <Box id="insurance" sx={{ bgcolor: brand.surfaceWarm, overflow: 'hidden', pb: { xs: 8, md: 10 } }} className="component-box component-box--flush">
      <Box sx={{ ...pageHeroRichSxFor(pageHeroImages.insurance), textAlign: 'center' }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography component="p" sx={{ ...pageHeroType.eyebrow, mb: 2.5, textAlign: 'center' }}>
            SECURE YOUR TOMORROW
          </Typography>
          <Typography component="h1" sx={{ ...pageHeroType.title, mb: 2.5, textAlign: 'center' }}>
            Insurance <Box component="span" sx={{ color: '#fde68a' }}>Solutions</Box>
          </Typography>
          <Typography sx={{ ...pageHeroType.subtitle, mb: 4, maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            Life is unpredictable, but your coverage shouldn&apos;t be. Get comprehensive insurance plans tailored to your needs for a worry-free future.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="#/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '999px',
              fontWeight: 800,
              bgcolor: '#fbbf24',
              color: '#422006',
              boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
              '&:hover': { bgcolor: '#fcd34d' },
            }}
          >
            Get a Quote
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 8 } }}>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: brand.slate, letterSpacing: '-0.02em' }}>
              Why Choose Our <Box component="span" sx={{ color: brand.accentDark }}>Insurance?</Box>
            </Typography>
            <Typography paragraph color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}>
              We partner with India&apos;s leading insurance providers to bring you the best plans with high claim settlement ratios, affordable premiums, and hassle-free processes.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ ...pageElevatedCardSx, p: 4, height: '100%', borderLeft: `6px solid ${brand.primary}` }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, color: brand.primary, mb: 3 }}>
                Our Portfolio
              </Typography>
              <List disablePadding>
                <ListItem sx={{ mb: 2, bgcolor: brand.surface, borderRadius: 2, py: 2 }}>
                  <ListItemIcon>
                    <VerifiedUserIcon color="primary" fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6" fontWeight={800} color={brand.slate}>General Insurance</Typography>}
                    secondary="Motor, Health, Travel, Home, and Business Insurance."
                  />
                </ListItem>
                <ListItem sx={{ bgcolor: brand.surface, borderRadius: 2, py: 2 }}>
                  <ListItemIcon>
                    <HealthAndSafetyIcon color="primary" fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6" fontWeight={800} color={brand.slate}>Life Insurance</Typography>}
                    secondary="Term Life, Whole Life, Endowment Plans, and ULIPs."
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 10 } }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 2, color: brand.slate, letterSpacing: '-0.02em' }}>
          Comprehensive <Box component="span" sx={{ color: brand.primary }}>Coverage</Box>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                ...pageElevatedCardSx,
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 28px 56px rgba(15, 23, 42, 0.12)' },
              }}
            >
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(15, 118, 110, 0.15)', color: brand.primary, mx: 'auto', mb: 2 }}>
                <DirectionsCarIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, mb: 2, color: brand.slate }}>Motor Insurance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Comprehensive coverage for your Car or Bike. Protection against accidents, theft, fire, and third-party liabilities. Cashless claims at network garages.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                ...pageElevatedCardSx,
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 28px 56px rgba(15, 23, 42, 0.12)' },
              }}
            >
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(180, 83, 9, 0.15)', color: brand.accentDark, mx: 'auto', mb: 2 }}>
                <HealthAndSafetyIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, mb: 2, color: brand.slate }}>Health Insurance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Secure your family&apos;s health with plans covering hospitalization, pre/post-medical expenses, and critical illnesses. Tax benefits under section 80D.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                ...pageElevatedCardSx,
                p: 4,
                textAlign: 'center',
                height: '100%',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 28px 56px rgba(15, 23, 42, 0.12)' },
              }}
            >
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(55, 48, 163, 0.12)', color: brand.indigo, mx: 'auto', mb: 2 }}>
                <FlightTakeoffIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, mb: 2, color: brand.slate }}>Travel Insurance</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Travel worry-free! Covers trip cancellations, lost baggage, medical emergencies abroad, and flight delays. Essential for international travel.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ mt: { xs: 8, md: 10 }, py: { xs: 8, md: 10 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: brand.slate, letterSpacing: '-0.02em' }}>
                Why Do You Need <Box component="span" sx={{ color: brand.accentDark }}>Insurance?</Box>
              </Typography>
              <List>
                {[
                  'Financial Security for your family in your absence.',
                  'Protection against rising medical costs.',
                  'Mandatory by law (Motor Insurance).',
                  'Wealth creation and tax saving benefits.',
                  'Peace of mind knowing you are covered.',
                ].map((text, index) => (
                  <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <VerifiedUserIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={text} primaryTypographyProps={{ fontSize: '1.05rem', fontWeight: 500, color: brand.muted, lineHeight: 1.65 }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ ...pageElevatedCardSx, p: 4, background: brand.ctaGradient, color: 'white', border: 'none', height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 800 }}>
                  Did You Know?
                </Typography>
                <Typography paragraph sx={{ opacity: 0.92, lineHeight: 1.75 }}>
                  Medical inflation in India is rising at 14% annually. A single hospitalization can wipe out years of savings.
                </Typography>
                <Typography paragraph sx={{ opacity: 0.92, lineHeight: 1.75 }}>
                  Having adequate Health and Life Insurance is the first step towards a secure financial plan.
                </Typography>
                <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(255,255,255,0.22)' }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#fde68a' }}>98.5%</Typography>
                  <Typography variant="body1">Claim Settlement Ratio of our partners</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 8, md: 10 } }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, mb: 2, color: brand.slate, letterSpacing: '-0.02em' }}>
          Insurance <Box component="span" sx={{ color: brand.primary }}>FAQs</Box>
        </Typography>
        <Grid container spacing={3}>
          {[
            { q: 'What is the right age to buy Health Insurance?', a: 'The earlier, the better. Buying young ensures lower premiums and coverage for illnesses that might develop later in life.' },
            { q: 'Is Motor Insurance mandatory?', a: 'Yes, Third-Party Motor Insurance is mandatory by law in India for all vehicles plying on public roads.' },
            { q: 'Can I save tax with Insurance?', a: 'Yes, Life Insurance premiums are eligible for deduction under Section 80C, and Health Insurance premiums under Section 80D of the Income Tax Act.' },
            { q: 'What is a cashless claim?', a: 'In a cashless claim, the insurance company settles the bill directly with the network hospital/garage, so you don’t have to pay upfront.' },
          ].map((faq, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={0}
                sx={{
                  ...pageElevatedCardSx,
                  p: 3,
                  height: '100%',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': { borderColor: brand.primary, boxShadow: '0 20px 48px rgba(15, 118, 110, 0.12)' },
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: brand.primary }}>
                  {faq.q}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {faq.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Insurance;
